import type { UserActor } from "@corpuscomplete/core/actor";
import { accessDenied } from "@corpuscomplete/core/utils";
import type { Condition, ExpressionBuilder, Transaction } from "@rocicorp/zero";

import { SpaceScopePolicy } from "../space/scope-policy";
import { MealPlanningSessionScopePolicy } from "../../nutrition/meal-planning-session/scope-policy";
import { Scope } from ".";
import { type Schema } from "../../zero/schema";

type ScopeCondition = ReturnType<ExpressionBuilder<"scope", Schema>["cmp"]>;

export namespace ScopePolicy {
  export function viewable(
    eb: ExpressionBuilder<"scope", Schema>,
    actor: UserActor,
    kinds: readonly Scope.Kind[],
  ) {
    return policyExpression(eb, actor, kinds, "view");
  }

  export function editable(
    eb: ExpressionBuilder<"scope", Schema>,
    actor: UserActor,
    kinds: readonly Scope.Kind[],
  ) {
    return policyExpression(eb, actor, kinds, "edit");
  }

  export async function requireWritable(
    tx: Transaction,
    actor: UserActor,
    scopeId: string,
    kinds: readonly Scope.Kind[],
  ): Promise<void> {
    if (tx.location === "client") {
      return;
    }

    const scope = await Scope.byId(tx, scopeId);
    const canWriteScope = await canWrite(tx, actor, scope, kinds);
    if (!canWriteScope) {
      accessDenied("Cannot manage resources for this scope: " + scope.kind);
    }
  }

  async function canWrite(
    tx: Transaction,
    actor: UserActor,
    scope: Scope.Info,
    kinds: readonly Scope.Kind[],
  ): Promise<boolean> {
    if (!kinds.includes(scope.kind)) {
      return false;
    }
    if (scope.kind === "user") {
      return scope.userId === actor.userId;
    }
    if (scope.kind === "space") {
      return SpaceScopePolicy.canWrite(tx, actor, scope);
    }
    return MealPlanningSessionScopePolicy.canWrite(tx, actor, scope);
  }

  function policyExpression(
    eb: ExpressionBuilder<"scope", Schema>,
    actor: UserActor,
    kinds: readonly Scope.Kind[],
    mode: "view" | "edit",
  ): ScopeCondition {
    const conditions: Condition[] = [];

    if (kinds.includes("user")) {
      conditions.push(eb.cmp("userId", "=", actor.userId));
    }

    if (kinds.includes("space")) {
      conditions.push(
        eb.exists(
          "space",
          (space) =>
            space.where((spaceEb) =>
              mode === "view"
                ? SpaceScopePolicy.viewable(spaceEb, actor)
                : SpaceScopePolicy.editable(spaceEb, actor),
            ),
          { flip: true },
        ),
      );
    }

    if (kinds.includes("mealPlanningSession")) {
      conditions.push(
        eb.exists(
          "mealPlanningSession",
          (session) =>
            session.where((sessionEb) =>
              mode === "view"
                ? MealPlanningSessionScopePolicy.viewable(sessionEb, actor)
                : MealPlanningSessionScopePolicy.editable(sessionEb, actor),
            ),
          { flip: true },
        ),
      );
    }

    return eb.or(...conditions);
  }
}
